/**
 * Courses API endpoints
 */

import { fetchApi, buildQueryString, withPagination } from '../core';
import type {
  QueryParams,
  CreateCourseData,
  UpdateCourseData,
  CourseLaunchReadinessResponse,
  CourseCancellationPreview,
  CloseCoursePreconditions,
  CourseLifecycleActionResponse,
  CourseCompletionPolicy,
  UpsertCourseCompletionPolicy,
  StudentCourseHomeResponse,
  InstructorCourseHomeResponse,
} from '../types';
import type { Course, PaginatedResponse } from '@/types';
import { objectIdPath } from '../path';

type CourseListResponse = PaginatedResponse<Course[]> | Course[];

export function getAll(params?: QueryParams, componentName: string = 'Unknown') {
  const paginatedParams = withPagination(params);
  return fetchApi<CourseListResponse>(`/courses${buildQueryString(paginatedParams)}`, {}, componentName);
}

export function getById(id: string, componentName: string = 'Unknown') {
  return fetchApi<Course>(`/courses/${objectIdPath(id, 'course')}`, {}, componentName);
}

export function create(data: CreateCourseData, componentName: string = 'Unknown') {
  return fetchApi<Course>('/courses', {
    method: 'POST',
    body: JSON.stringify(data)
  }, componentName);
}

export function update(id: string, data: UpdateCourseData, componentName: string = 'Unknown') {
  return fetchApi<Course>(`/courses/${objectIdPath(id, 'course')}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }, componentName);
}

export function getMy(params?: QueryParams, componentName: string = 'Unknown') {
  const paginatedParams = withPagination(params);
  return fetchApi<CourseListResponse>(`/my/courses${buildQueryString(paginatedParams)}`, {}, componentName);
}

export function addInstructor(courseId: string, instructorId: string, componentName: string = 'Unknown') {
  return fetchApi(`/courses/${objectIdPath(courseId, 'course')}/instructors`, {
    method: 'POST',
    body: JSON.stringify({ instructor_id: instructorId })
  }, componentName);
}

export function removeInstructor(courseId: string, instructorId: string, componentName: string = 'Unknown') {
  return fetchApi(`/courses/${objectIdPath(courseId, 'course')}/instructors/${objectIdPath(instructorId, 'instructor')}`, {
    method: 'DELETE'
  }, componentName);
}

export function getLaunchReadiness(id: string, componentName: string = 'Unknown') {
  return fetchApi<CourseLaunchReadinessResponse>(
    `/courses/${objectIdPath(id, 'course')}/launch-readiness`,
    {},
    componentName
  );
}

export function launch(id: string, componentName: string = 'Unknown') {
  return fetchApi<CourseLifecycleActionResponse>(`/courses/${objectIdPath(id, 'course')}/launch`, {
    method: 'POST',
  }, componentName);
}

export function close(
  id: string,
  reason: string,
  componentName: string = 'Unknown',
  preconditions?: CloseCoursePreconditions
) {
  return fetchApi<CourseLifecycleActionResponse>(`/courses/${objectIdPath(id, 'course')}/close`, {
    method: 'POST',
    body: JSON.stringify({ reason, ...preconditions }),
  }, componentName);
}

export function getCancellationPreview(id: string, componentName: string = 'Unknown') {
  return fetchApi<CourseCancellationPreview>(
    `/courses/${objectIdPath(id, 'course')}/cancellation-preview`,
    {},
    componentName
  );
}

export function getCompletionPolicy(id: string, componentName: string = 'Unknown') {
  return fetchApi<CourseCompletionPolicy | null>(
    `/courses/${objectIdPath(id, 'course')}/completion-policy`,
    {},
    componentName
  );
}

export function updateCompletionPolicy(
  id: string,
  data: UpsertCourseCompletionPolicy,
  componentName: string = 'Unknown'
) {
  return fetchApi<CourseCompletionPolicy>(`/courses/${objectIdPath(id, 'course')}/completion-policy`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }, componentName);
}

export function getStudentHome(id: string, componentName: string = 'Unknown') {
  return fetchApi<StudentCourseHomeResponse>(
    `/courses/${objectIdPath(id, 'course')}/student-home`,
    {},
    componentName
  );
}

export function getInstructorHome(id: string, componentName: string = 'Unknown') {
  return fetchApi<InstructorCourseHomeResponse>(
    `/courses/${objectIdPath(id, 'course')}/instructor-home`,
    {},
    componentName
  );
}
